<template>
  <div class="create-client">
    <div v-if="!wizardMode" class="create-client__header">
      <h1 class="create-client__title">New Client</h1>
    </div>

    <themed-card class="create-client__form-card">
      <div v-if="!dataLoaded" class="create-client__loading">
        <theme-icon icon="loading" type="icons" classes="w-8 h-8 animate-spin" />
        <p>Loading form...</p>
      </div>

      <themed-form
        v-else
        :fields="formFields"
        :model-value="client"
        @input="client = $event"
        :errors="errors"
        layout="grid"
        :columns="4"
      />

      <div class="create-client__actions">
        <themed-button
          @click="save"
          :disabled="saving || !dataLoaded"
          variant="default"
        >
          <theme-icon v-if="saving" icon="loading" type="icons" classes="w-4 h-4 animate-spin" />
          <span>{{ wizardMode ? 'Continue' : 'Create Client' }}</span>
        </themed-button>
      </div>
    </themed-card>
  </div>
</template>

<script>
import ThemedCard from '../ThemedCard.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'CreateClient',

  components: {
    ThemedCard,
    ThemedForm,
    ThemedButton,
    ThemeIcon
  },

  props: {
    wizardMode: {
      type: Boolean,
      default: false
    },
    postUrl: {
      type: String,
      default: '/clients'
    }
  },

  data() {
    return {
      client: {
        name: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country_id: 16 // Default to USA
      },
      errors: {},
      countries: [],
      states: [],
      saving: false,
      dataLoaded: false
    };
  },

  computed: {
    formFields() {
      // Don't render fields until data is loaded
      if (!this.dataLoaded) {
        return [];
      }

      return [
        {
          name: 'name',
          label: 'Name',
          type: 'input',
          required: true,
          col: 4,
        },
        {
          name: 'address',
          label: 'Address',
          type: 'input',
          required: true,
          col: 4,
        },
        {
          name: 'city',
          label: 'City',
          type: 'input',
          required: true,
          col: 1,
        },
        {
          name: 'state',
          label: 'State',
          type: 'select',
          required: true,
          col: 1,
          options: this.stateOptions,
        },
        {
          name: 'postal_code',
          label: 'Postal Code',
          type: 'input',
          required: true,
          col: 1,
        },
        {
          name: 'country_id',
          label: 'Country',
          type: 'select',
          required: true,
          col: 1,
          options: this.countryOptions,
        }
      ];
    },

    countryOptions() {
      return this.countries.map(country => ({
        label: country.name,
        value: country.id
      }));
    },

    stateOptions() {
      return this.states.map(state => ({
        label: state.state,
        value: state.state
      }));
    }
  },

  mounted() {
    this.fetchAddressInfo();
  },

  methods: {
    async fetchAddressInfo() {
      try {
        const response = await axios.get('/api/address-info');
        this.countries = response.data.countries || [];
        this.states = response.data.states || [];
        this.dataLoaded = true;
      } catch (error) {
        console.error('Failed to fetch address info:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load address information'
        });
      }
    },

    async save() {
      this.errors = {};
      this.saving = true;

      try {
        const response = await axios.post(this.postUrl, this.client);

        if (this.wizardMode) {
          // Emit event for wizard mode
          this.$emit('client-saved', response.data.client);
        } else {
          // Show success notification
          this.$notify({
            type: 'success',
            title: 'Success',
            text: response.data.message || 'Client created successfully'
          });

          // Redirect to client page after short delay
          setTimeout(() => {
            window.location.href = `/clients/${response.data.client.id}`;
          }, 1000);
        }
      } catch (error) {
        this.saving = false;

        if (error.response && error.response.data && error.response.data.errors) {
          // Validation errors
          this.errors = error.response.data.errors;
        } else {
          // General error
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Failed to create client'
          });
        }
      }
    }
  }
};
</script>
